import DialogContentText from "@material-ui/core/DialogContentText";
import ActionDialog from "@saleor/components/ActionDialog";
import { ConfirmButtonTransitionState } from "@saleor/components/ConfirmButton/ConfirmButton";
import DeleteFilterTabDialog from "@saleor/components/DeleteFilterTabDialog";
import SaveFilterTabDialog, {
  SaveFilterTabDialogFormData
} from "@saleor/components/SaveFilterTabDialog";
import {
  DEFAULT_INITIAL_PAGINATION_DATA,
  DEFAULT_INITIAL_SEARCH_DATA
} from "@saleor/config";
import useDownloadReport from "@saleor/hooks/useDownloadReport";
import useListSettings from "@saleor/hooks/useListSettings";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import usePaginator, {
  createPaginationState
} from "@saleor/hooks/usePaginator";
import { getStringOrPlaceholder, maybe } from "@saleor/misc";
import { InvalidThreeMonth } from "@saleor/motherOrders/utils/handlers";
import useMallSearch from "@saleor/searches/useMallSearch";
import { ListViews } from "@saleor/types";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import createFilterHandlers from "@saleor/utils/handlers/filterHandlers";
import Moment from "moment";
import React, { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { reportsDownload } from "../../../utils/utils";
import GrabExpressListPage from "../../components/GrabExpressListPage";
import {
  useGrabExpressDownloadQuery,
  useGrabExpressListQuery,
  useInitialGrabExpressFilterDataQuery
} from "../../queries";
import {
  grabExpressListUrl,
  GrabExpressListUrlDialog,
  GrabExpressUrlQueryParams
} from "../../url";
import { filterReportData } from "../../utils";
import {
  areFiltersApplied,
  deleteFilterTab,
  getActiveFilters,
  getFilterOpts,
  getFilterQueryParam,
  getFilterTabs,
  getFilterVariables,
  saveFilterTab
} from "./filters";

interface GrabExpressListProp {
  params: GrabExpressUrlQueryParams;
}

const GrabExpressList: React.FC<GrabExpressListProp> = ({ params }) => {
  const navigate = useNavigator();
  const paginate = usePaginator();
  const notify = useNotifier();
  const { updateListSettings, settings } = useListSettings(
    ListViews.REPORTS_LIST
  );
  const paginationState = createPaginationState(settings.rowNumber, params);
  const currencySymbol = "PHP";
  const intl = useIntl();
  const dateNow = Moment(new Date()).format("YYYYMMDDHHmmss");

  const sheetName = "Grab Express";
  const fileName = `${reportsDownload.grabExpressOmi.filename}_${dateNow}.xlsx`;
  const [loadData, setLoadData] = useState(true);
  const [downloadPagination, setDownloadPagination] = useState("");
  const [downloadStatus, setDownloadStatus] = useState<
    ConfirmButtonTransitionState
  >("default");

  const queryVariables = React.useMemo(
    () => ({
      ...paginationState,
      filter: getFilterVariables(params)
    }),

    [params]
  );

  const tabs = getFilterTabs();

  const currentTab =
    params.activeTab === undefined
      ? areFiltersApplied(params)
        ? tabs.length + 1
        : 0
      : parseInt(params.activeTab, 0);

  const [
    changeFilters,
    resetFilters,
    handleSearchChange
  ] = createFilterHandlers({
    createUrl: grabExpressListUrl,
    getFilterQueryParam,
    navigate,
    params
  });

  const searchMall = useMallSearch({
    variables: {
      ...DEFAULT_INITIAL_SEARCH_DATA,
      first: 5
    }
  });

  const filterOpts = getFilterOpts(params, {
    initial: maybe(
      () => initialFilterData.malls.edges.map(edge => edge.node) || []
    ),
    search: searchMall
  });

  const [openModal, closeModal] = createDialogActionHandlers<
    GrabExpressListUrlDialog,
    GrabExpressUrlQueryParams
  >(navigate, grabExpressListUrl, params);

  const handleTabChange = (tab: number) => {
    navigate(
      grabExpressListUrl({
        activeTab: tab.toString(),
        ...tabs[tab - 1]?.data
      })
    );
  };

  const { data: orders, loading } = useGrabExpressListQuery({
    displayLoader: true,
    skip: Object.keys(params).length === 0,
    variables: queryVariables
  });

  const { data: initialFilterData } = useInitialGrabExpressFilterDataQuery({
    variables: {
      mall: params.mall
    }
  });

  const reportVariables = React.useMemo(
    () => ({
      after: downloadPagination,
      filter: getFilterVariables(params),
      first: 1200
    }),
    [params]
  );

  React.useEffect(
    () =>
      navigate(
        grabExpressListUrl({
          ...params,
          ...DEFAULT_INITIAL_PAGINATION_DATA
        }),
        true
      ),
    [settings.rowNumber]
  );

  // Todo - <Marco>: Explore on how to fix from BE
  React.useEffect(() => {
    if (params.query !== null) {
      params.after = null;
      params.before = null;
    }
  }, [params]);

  const {
    data: reportData,
    loading: reportLoading,
    refetch: refetchDownload
  } = useGrabExpressDownloadQuery({
    displayLoader: true,
    skip: loadData,
    variables: reportVariables
  });

  const { loadNextPage, loadPreviousPage, pageInfo } = paginate(
    orders?.grabExpress?.pageInfo,
    paginationState,
    params
  );

  const handleTabSave = (data: SaveFilterTabDialogFormData) => {
    saveFilterTab(data.name, getActiveFilters(params));
    handleTabChange(tabs.length + 1);
  };

  const handleTabDelete = () => {
    deleteFilterTab(currentTab);
    navigate(grabExpressListUrl());
  };

  const [downloadData, setDownloadData] = useState([null]);

  const allData = reportData?.grabExpress?.edges.map(edge => edge.node);
  const totalCount = reportData?.grabExpress?.totalCount;
  const nextPage = reportData?.grabExpress?.pageInfo;

  React.useEffect(() => {
    if (reportData && !reportLoading) {
      setDownloadData(current => [...current, allData]);
      setDownloadPagination(nextPage?.endCursor);
      const totalDownload = downloadData?.flat();
      if (totalDownload && totalDownload?.length >= totalCount) {
        const selectedReportData = filterReportData(
          downloadData?.flat(),
          reportsDownload.grabExpressOmi.fields
        );
        useDownloadReport(
          selectedReportData,
          reportsDownload.grabExpressOmi.fields,
          sheetName,
          fileName
        );
        setDownloadStatus("success");
        setDownloadPagination("");
        setLoadData(true);
        setDownloadData([null]);
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Grab Express Report successfully downloaded."
          })
        });
        closeModal();
      }
    }
  }, [reportData]);

  const cancelDownload = () => {
    setLoadData(true);
    setDownloadData([null]);
    setDownloadStatus("default");
    setDownloadPagination("");
    closeModal();
  };

  React.useEffect(() => {
    if (
      InvalidThreeMonth(
        params.orderCompleteDateFrom,
        params.orderCompleteDateTo
      ) ||
      InvalidThreeMonth(params.createdFrom, params.createdTo)
    ) {
      notify({
        status: "error",
        text: intl.formatMessage({
          defaultMessage:
            "Unable to filter the report data. Please limit the date range to 3 months."
        })
      });
      navigate(grabExpressListUrl());
    }
  }, [params]);

  return (
    <div>
      <GrabExpressListPage
        currencySymbol={currencySymbol}
        currentTab={currentTab}
        disabled={loading}
        initialSearch={params.query || ""}
        filterOpts={filterOpts}
        orders={orders}
        onAll={resetFilters}
        onFilterChange={changeFilters}
        onSearchChange={handleSearchChange}
        onTabChange={handleTabChange}
        onTabDelete={() => openModal("delete-search")}
        onTabSave={() => openModal("save-search")}
        onNextPage={loadNextPage}
        onPreviousPage={loadPreviousPage}
        onUpdateListSettings={updateListSettings}
        pageInfo={pageInfo}
        params={params}
        tabs={tabs}
        settings={settings}
        onRowClick={() => handleTabDelete}
        download={() => openModal("download")}
      />

      <ActionDialog
        open={params.action === "download"}
        onClose={cancelDownload}
        confirmButtonState={downloadStatus}
        onConfirm={() => {
          refetchDownload();
          setLoadData(false);
          setDownloadStatus("loading");
          setDownloadData([]);
          setDownloadPagination("");
        }}
        variant="default"
        title={intl.formatMessage({
          defaultMessage: "Download Grab Express",
          description: "dialog title"
        })}
      >
        <DialogContentText>
          <FormattedMessage
            defaultMessage="You are about to download a grab express report with {total} rows. Do you want to continue?"
            values={{
              total: <strong>{orders?.grabExpress?.totalCount}</strong>
            }}
          />
        </DialogContentText>
      </ActionDialog>

      <SaveFilterTabDialog
        open={params.action === "save-search"}
        confirmButtonState="default"
        onClose={closeModal}
        onSubmit={handleTabSave}
      />

      <DeleteFilterTabDialog
        open={params.action === "delete-search"}
        confirmButtonState="default"
        onClose={closeModal}
        onSubmit={handleTabDelete}
        tabName={getStringOrPlaceholder(tabs[currentTab - 1]?.name)}
      />
    </div>
  );
};

export default GrabExpressList;
